/**
 * Comprime una imagen en el navegador (mismo criterio que Control de Armamento):
 * redimensiona a max 1280×1280 y JPEG calidad 0.8 para bajar peso sin perder legibilidad.
 *
 * @param {File|Blob} file
 * @param {{ maxWidth?: number, maxHeight?: number, quality?: number }} [opts]
 * @returns {Promise<File>}
 */
export function compressImage(file, opts = {}) {
  const maxWidth = opts.maxWidth ?? 1280;
  const maxHeight = opts.maxHeight ?? 1280;
  const quality = opts.quality ?? 0.8;

  return new Promise((resolve) => {
    try {
      const image = new Image();
      const objectUrl = URL.createObjectURL(file);

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        let { width, height } = image;
        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file instanceof File ? file : new File([file], 'imagen.jpg', { type: 'image/jpeg' }));
          return;
        }

        ctx.drawImage(image, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file instanceof File ? file : new File([file], 'imagen.jpg', { type: 'image/jpeg' }));
              return;
            }
            const baseName =
              file instanceof File && file.name
                ? file.name.replace(/\.[^.]+$/, '')
                : 'imagen';
            resolve(
              new File([blob], `${baseName}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now()
              })
            );
          },
          'image/jpeg',
          quality
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(file instanceof File ? file : new File([file], 'imagen.jpg', { type: 'image/jpeg' }));
      };

      image.src = objectUrl;
    } catch (err) {
      console.warn('Error al comprimir imagen, se usará el original:', err);
      resolve(file instanceof File ? file : new File([file], 'imagen.jpg', { type: 'image/jpeg' }));
    }
  });
}

/**
 * Valida tipo imagen, comprime siempre y comprueba el tope en bytes tras comprimir.
 *
 * @param {File} file
 * @param {{ maxBytes: number, label?: string }} options
 * @returns {Promise<{ ok: true, file: File } | { ok: false, error: string }>}
 */
export async function prepareImageForUpload(file, { maxBytes, label = 'imagen' }) {
  if (!file) {
    return { ok: false, error: `Debe adjuntar la ${label}.` };
  }
  if (!file.type || !file.type.startsWith('image/')) {
    return { ok: false, error: `Solo se permiten imágenes para la ${label}.` };
  }

  const compressed = await compressImage(file);
  if (compressed.size > maxBytes) {
    const maxMb = (maxBytes / (1024 * 1024)).toFixed(0);
    return {
      ok: false,
      error: `La ${label} supera el máximo de ${maxMb} MB aunque se comprimió. Use otra foto o baje la resolución.`
    };
  }
  return { ok: true, file: compressed };
}
